import {
  HttpErrorResponse,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { AuthService } from "@modules/auth";
import { BehaviorSubject, throwError } from "rxjs";
import { catchError, filter, switchMap, take } from "rxjs/operators";
import { ELanguages } from "../enums/Languages.enum";
import { ELocalStorage } from "../enums/LocalStorage.enum";
import { EReqHeaders } from "../enums/RequestHeaders";
import { LocalService } from "../services/local-storage.service";
import { ToastService } from "../services/toast.service";
import { SystemConfigurationsService } from "@modules/management/services/system-configurations.service";

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<string | null> =
    new BehaviorSubject<string | null>(null);
  private _localStorageService = inject(LocalService);
  private _authService = inject(AuthService);
  private _toastService = inject(ToastService);
  private _systemConfigService = inject(SystemConfigurationsService);

  // Flag to suppress duplicate toasts
  private isToastVisible = false;

  intercept(request: HttpRequest<unknown>, next: HttpHandler) {
    const access_token =
      this._localStorageService.getData(ELocalStorage.ACCESS_TOKEN) || "";
    const companyId =
      this._localStorageService.getData(ELocalStorage.COMPANY_ID) || 0;
    const language =
      localStorage.getItem(ELocalStorage.LANGUAGE) || ELanguages.UZ_CYRL;

    const modifiedHeaders = request.headers
      .append(EReqHeaders.AUTHORIZATION, `Bearer ${access_token}`)
      .append(EReqHeaders.COMPANY_ID, companyId.toString())
      .append(EReqHeaders.LANGUAGE, language)
      .append(EReqHeaders.CHANNEL, "WEB");

    const modifiedRequest = request.clone({ headers: modifiedHeaders });

    return next.handle(modifiedRequest).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 400) {
          this.showErrorToast(() => {
            this.handleBadRequest(error);
          });
        } else if (error.status === 500) {
          this.showErrorToast(() => {
            this._toastService.error("ERROR_LIST.SERVER_ERROR");
          });
        } else if (error.status === 409) {
          this.showErrorToast(() => {
            this._toastService.error(
              error.error.message ?? "ERROR_LIST.BAD_REQUEST"
            );
          });
        } else if (error.status === 401) {
          return this.handleUnauthorizedError(modifiedRequest, next);
        } else if (error.status === 403) {
          this.showErrorToast(() => {
            this._toastService.error("ERROR_LIST.ACCESS_DENIED");
          });
          this._authService
            .getUserPermissions()
            .pipe(
              switchMap(() =>
                this._systemConfigService.checkPermissionForLogout()
              )
            )
            .subscribe();
        }
        return throwError(() => error);
      })
    );
  }

  private handleUnauthorizedError(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ) {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      return this._authService.updateAccessToken().pipe(
        switchMap(() => {
          const updatedToken =
            this._localStorageService.getData(ELocalStorage.ACCESS_TOKEN) || "";
          this.isRefreshing = false;
          this.refreshTokenSubject.next(updatedToken);

          const updatedHeaders = request.headers.set(
            EReqHeaders.AUTHORIZATION,
            `Bearer ${updatedToken}`
          );
          const updatedRequest = request.clone({ headers: updatedHeaders });
          return next.handle(updatedRequest);
        }),
        catchError((error) => {
          this.isRefreshing = false;
          this._authService.successLogout();
          return throwError(() => error);
        })
      );
    } else {
      return this.refreshTokenSubject.pipe(
        filter((token) => token !== null),
        take(1),
        switchMap(() => {
          const updatedToken =
            this._localStorageService.getData(ELocalStorage.ACCESS_TOKEN) || "";
          const updatedHeaders = request.headers.set(
            EReqHeaders.AUTHORIZATION,
            `Bearer ${updatedToken}`
          );
          const updatedRequest = request.clone({ headers: updatedHeaders });
          return next.handle(updatedRequest);
        })
      );
    }
  }

  private handleBadRequest(error: HttpErrorResponse) {
    if (
      error.error instanceof Blob &&
      error.error.type === "application/json"
    ) {
      const reader = new FileReader();
      reader.onload = () => {
        try {
          const jsonError = JSON.parse(reader.result as string);
          this._toastService.error(
            jsonError.message ?? "ERROR_LIST.BAD_REQUEST"
          );
        } catch (parseError) {
          this._toastService.error(
            error.error.message ?? "ERROR_LIST.BAD_REQUEST"
          );
        }
      };
      reader.onerror = () => {
        this._toastService.error(
          error.error.message ?? "ERROR_LIST.BAD_REQUEST"
        );
      };
      reader.readAsText(error.error);
    } else {
      this._toastService.error(error.error.message ?? "ERROR_LIST.BAD_REQUEST");
    }
  }

  private showErrorToast(showToastCallback: () => void) {
    if (!this.isToastVisible) {
      this.isToastVisible = true;

      showToastCallback();

      setTimeout(() => {
        this.isToastVisible = false;
      }, 500);
    }
  }
}
